import styles from "./inbox-tab.module.css";

import { useState, useEffect, useContext } from "react";
import { Flex, Button, App, Spin, Pagination, Collapse } from "antd";

import NoticesGroupsTable from "@/components/tables/NoticesGroupsTable";
// import SearchFrom3 from "@/components/inputs/SearchFrom3";
import CustomSelect from "@/components/inputs/CustomSelect";

import DotCircleEmpty from "@/icons/DotCircleEmpty";
import TrashIcon from "@/icons/TrashIcon";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { UserContext } from "@/context/UserContext";

import {
  getList,
  getNotificationTypes,
  getNotificationRules,
  readNotices,
  deleteNotices,
  unreadNotices,
} from "@/api/notices";
import FilterIcon from "@/icons/FilterIcon";

const LOCAL_STORAGE_KEY = "notices";
const READ_OPTIONS = [
  {
    label: "Все уведомления",
    value: "",
  },
  {
    label: "Прочитанные",
    value: "read",
  },
  {
    label: "Непрочитанные",
    value: "unread",
  },
];

const NoticesFilters = ({
  className,
  tableParams,
  handleFilterChange,
  notificationTypes,
  notificationRules,
}) => (
  <Flex gap={8} align="center" wrap className={className}>
    {/* <SearchFrom3
            placeholder="Поиск"
            defaultValue={tableParams.filters.search}
            onSearch={(search) => handleFilterChange("search", search)}
          /> */}
    <CustomSelect
      className={styles.filter}
      placeholder="Все уведомления"
      options={READ_OPTIONS}
      style={{
        width: 160,
      }}
      placement="bottomLeft"
      popupMatchSelectWidth={false}
      value={tableParams.filters.unread_only}
      onChange={(unreadOnly) => handleFilterChange("unread_only", unreadOnly)}
    />
    <CustomSelect
      className={styles.filter}
      placeholder="Раздел"
      options={notificationTypes}
      fieldNames={{
        label: "name",
        value: "uuid",
        key: "uuid",
      }}
      style={{
        width: 100,
      }}
      showSearch
      optionFilterProp="name"
      allowClear
      placement="bottomLeft"
      popupMatchSelectWidth={false}
      value={tableParams.filters.notification_type}
      onChange={(notification_type) =>
        handleFilterChange("notification_type", notification_type)
      }
    />

    <CustomSelect
      className={styles.filter}
      placeholder="Событие"
      options={notificationRules}
      fieldNames={{
        label: "name",
        value: "uuid",
        key: "uuid",
      }}
      style={{
        width: 150,
      }}
      showSearch
      optionFilterProp="name"
      allowClear
      placement="bottomLeft"
      popupMatchSelectWidth={false}
      value={tableParams.filters.notification_rule}
      onChange={(notification_rule) =>
        handleFilterChange("notification_rule", notification_rule)
      }
    />
  </Flex>
);

const InboxTab = () => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [optionsPending, setOptionsPending] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [notificationRules, setNotificationRules] = useState([]);

  const [selectedRows, setSelectedRows] = useState([]); // выбранные объекты

  const initialTableParams = {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
    filters: {
      // search: null,
      notification_type: null,
      notification_rule: null,
    },
    sort: "-created_at",
  };

  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  //  initial fetch
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    setOptionsPending(true);

    try {
      const [types, rules] = await Promise.all([
        getNotificationTypes(),
        getNotificationRules(),
      ]);

      setNotificationTypes(types);
      setNotificationRules(rules);

      // Инициализация параметров из localStorage
      const localParams =
        JSON.parse(
          localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`),
        ) || initialTableParams;

      setTableParams(localParams);
      fetchData(localParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  const fetchData = async (tParams) => {
    setIsLoading(true);

    const currentParams = tParams || tableParams;
    const params = {
      page: currentParams.pagination.current,
      perpage: currentParams.pagination.pageSize,
      sort: currentParams.sort,
      ...currentParams.filters,
    };

    try {
      const { notifications, total, page, perPage } = await getList(params);
      setData(notifications);

      const currentParams = tParams || tableParams;
      const updatedTableParams = {
        ...currentParams,
        pagination: {
          ...currentParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setParams(updatedTableParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
      JSON.stringify(params),
    );
  };

  const updateFilters = (localParams, newFilters, resetPage = false) => {
    const updatedFilters = { ...localParams.filters, ...newFilters };

    const updatedTableParams = {
      ...localParams,
      filters: updatedFilters,
      pagination: {
        ...localParams.pagination,
        current: resetPage ? 1 : localParams.pagination.current,
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
    setSelectedRows([]);
  };

  // Обработчик для смены фильтров
  const handleFilterChange = async (name, val) => {
    updateFilters(
      tableParams,
      {
        [name]: val || null,
      },
      true,
    );
  };

  // Обработчик изменения пагинации или сортировки, которые приходят из таблицы
  const handleTableChange = (pagination, filters, sorter) => {
    const isSortChanged =
      `${sorter.order === "descend" ? "-" : ""}${sorter.field || ""}` !==
      tableParams.sort;

    const updatedTableParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: isSortChanged
          ? 1
          : pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
      sort: `${sorter.order === "descend" ? "-" : ""}${sorter.field || ""}`,
    };
    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  const handlePaginationChange = (current, pageSize) => {
    if (pageSize !== tableParams.pagination.pageSize) {
      setSelectedRows([]);
    }
    const updatedTableParams = {
      ...tableParams,

      pagination: {
        ...tableParams.pagination,
        current,
        pageSize,
      },
    };
    setParams(updatedTableParams);

    fetchData(updatedTableParams);
  };

  const handleReadNotices = async (uuids) => {
    try {
      await readNotices(uuids || selectedRows);
      fetchData(tableParams);
      setSelectedRows([]);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  const handleUnreadNotices = async (uuid) => {
    try {
      await unreadNotices([uuid]);
      fetchData(tableParams);
      setSelectedRows([]);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  const handleDeleteNotices = async () => {
    let uuids = selectedRows || [];
    if (!selectedRows.length) {
      uuids = data.map((item) => item.uuid);
    }

    if (!uuids.length) return;

    try {
      await deleteNotices(uuids);
      fetchData(tableParams);
      setSelectedRows([]);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <Flex gap={15} vertical className="h-100">
          <Flex justify="space-between" align="center" wrap gap={20}>
            {/* Тут свернутые фильтры для мобильных */}
            <Collapse
              className={`w-100 collapse_no-padding ${styles.filtersCollapse}`}
              items={[
                {
                  key: "1",
                  label: (
                    <div>
                      <FilterIcon /> Фильтры
                    </div>
                  ),
                  children: (
                    <NoticesFilters
                      tableParams={tableParams}
                      handleFilterChange={handleFilterChange}
                      notificationTypes={notificationTypes}
                      notificationRules={notificationRules}
                    />
                  ),
                },
              ]}
              bordered={false}
              collapsible="header"
              ghost
            />
            {/* Тут фильтры для десктопа */}
            <NoticesFilters
              className={styles.filters}
              tableParams={tableParams}
              handleFilterChange={handleFilterChange}
              notificationTypes={notificationTypes}
              notificationRules={notificationRules}
            />

            <Flex gap={8} align="center">
              {!!selectedRows.length && (
                <>
                  <Button
                    type="primary"
                    icon={<DotCircleEmpty />}
                    loading={isLoading}
                    onClick={() => {
                      handleReadNotices();
                    }}
                  >
                    Отметить прочитанным
                  </Button>

                  <Button
                    type="primary"
                    icon={<TrashIcon />}
                    loading={isLoading}
                    onClick={() => {
                      handleDeleteNotices();
                    }}
                  >
                    Удалить
                  </Button>
                </>
              )}
            </Flex>
          </Flex>

          <div
            ref={tableContainerRef}
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <NoticesGroupsTable
              data={data}
              loading={isLoading}
              pagination={false}
              onChange={handleTableChange}
              selectedRowKeys={selectedRows}
              onRowsSelect={(selectedRowKeys) => {
                setSelectedRows(selectedRowKeys);
              }}
              onReadNotice={handleReadNotices}
              onUnreadNotice={handleUnreadNotices}
              scroll={{ x: "100%", y: tableHeight - headerHeight }}
            />
          </div>

          <div style={{ minHeight: 32 }}>
            <Pagination
              {...tableParams.pagination}
              onChange={handlePaginationChange}
            />
          </div>
        </Flex>
      )}
    </>
  );
};

export default InboxTab;
